import translate from '@translate';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select, { OnChangeType, SelectValueType } from 'src/components/Select';
import TextField from 'src/components/TextField';
import Grid, { GridItem } from 'src/components/Grid';
import Button from 'src/components/Button';
import getCountryOptions from 'src/utils/countryOptions';
import { sizes } from 'src/themes/default';
import { getDhlCountryMapCenter } from 'src/utils/dhlCountryMapCenters';
import { RootStoreType } from 'src/types/store';
import {
    clearDhlServicePoints,
    getDhlServicePointByIdFetch,
    getDhlServicePointsFetch,
} from 'src/resources/dhlServicePoints/reducer';
import DhlServicePointMap from 'src/components/DhlServicePointMap';

// Theme renders the input label above the field (input_top_PD) and the input box is
// body_LH plus 2 * 12.5px vertical padding; buttons in the same row copy that geometry.
const inputAlignedButtonSx = {
    flex: 1,
    marginTop: sizes.input_top_PD,
    height: `calc(${sizes.body_LH} + 25px)`,
};

export interface DhlServicePointSelectPropsType {
    onChange: OnChangeType;
    value: SelectValueType;
    error?: boolean;
    name?: string;
    label?: string;
    required?: boolean;
    disabled?: boolean;
    fullWidth?: boolean;
    /** Soft default only when no ADDRESS_ID is set yet (recipient address country). */
    recipientState?: string;
}

const DhlServicePointSelect: FC<DhlServicePointSelectPropsType> = (props) => {
    const dispatch = useDispatch();
    const options = useSelector((state: RootStoreType) => state.dhlServicePoints.data) || [];
    const points = useSelector((state: RootStoreType) => state.dhlServicePoints.points);
    const loading = useSelector((state: RootStoreType) => state.dhlServicePoints.loading);
    const countryOptions = useMemo(() => getCountryOptions(), []);

    const [country, setCountry] = useState<string>(props.recipientState?.toUpperCase() || 'CZ');
    const [postalCode, setPostalCode] = useState<string>('');
    const [city, setCity] = useState<string>('');
    const [mapOpen, setMapOpen] = useState(false);
    const [mapInitialCenter, setMapInitialCenter] = useState<[number, number]>(() =>
        getDhlCountryMapCenter(props.recipientState?.toUpperCase() || 'CZ')
    );
    const [mapAutoFitToPoints, setMapAutoFitToPoints] = useState(false);
    const [hydratedAddressId, setHydratedAddressId] = useState<string | null>(null);

    const applyCountryFromPoint = (servicePointId: string) => {
        const point = points.find((item) => item.servicePointId === servicePointId);
        if (point?.countryCode) {
            setCountry(point.countryCode.toUpperCase());
        }
    };

    // Re-open order detail / existing ADDRESS_ID → load point from DHL API and set search country from it
    useEffect(() => {
        if (!props.value) {
            setHydratedAddressId(null);
            return;
        }
        const addressId = String(props.value);
        if (hydratedAddressId === addressId) {
            return;
        }
        dispatch(getDhlServicePointByIdFetch(addressId));
        setHydratedAddressId(addressId);
    }, [props.value, hydratedAddressId, dispatch]);

    useEffect(() => {
        if (!props.value) {
            return;
        }
        applyCountryFromPoint(String(props.value));
        // eslint-disable-next-line react-hooks/exhaustive-deps -- react when points for selected id arrive
    }, [props.value, points]);

    useEffect(() => {
        return () => {
            dispatch(clearDhlServicePoints());
        };
    }, [dispatch]);

    const isSet = !props.value || options.filter((option) => option.value === props.value).length > 0;
    const allOptions = isSet
        ? options
        : [
              {
                  label: `${translate('messages.branchUnavailable')} (${props.value})`,
                  value: props.value,
              },
              ...options,
          ];

    const handleChangeCountry = (value: SelectValueType | SelectValueType[]) => {
        const nextCountry = String(Array.isArray(value) ? value[0] : value || '').toUpperCase();
        if (!nextCountry || country === nextCountry) {
            return;
        }
        setCountry(nextCountry);
        setPostalCode('');
        setCity('');
        if (props.value) {
            props.onChange(null, true);
        }
        dispatch(clearDhlServicePoints());
        setHydratedAddressId(null);
    };

    const trimmedPostalCode = postalCode.trim();
    const trimmedCity = city.trim();
    const hasAddressParams = Boolean(trimmedPostalCode || trimmedCity);

    const dispatchServicePointsSearch = (merge: boolean) => {
        if (!country) {
            return;
        }
        if (hasAddressParams) {
            dispatch(
                getDhlServicePointsFetch({
                    country,
                    postalCode: trimmedPostalCode || undefined,
                    city: trimmedCity || undefined,
                    merge,
                })
            );
            return;
        }
        const countryCenter = getDhlCountryMapCenter(country);
        dispatch(
            getDhlServicePointsFetch({
                country,
                latitude: countryCenter[0],
                longitude: countryCenter[1],
                merge,
            })
        );
    };

    const handleSearch = () => {
        if (!country) {
            return;
        }
        dispatchServicePointsSearch(false);
    };

    const handleOpenMap = () => {
        if (!country) {
            return;
        }
        const countryCenter = getDhlCountryMapCenter(country);
        const hasPoints = points.length > 0;

        setMapInitialCenter(countryCenter);

        // Prefer already entered PSČ/město so the user does not have to press Search first.
        if (hasAddressParams) {
            setMapAutoFitToPoints(true);
            setMapOpen(true);
            dispatchServicePointsSearch(false);
            return;
        }

        setMapAutoFitToPoints(hasPoints);
        setMapOpen(true);

        if (!hasPoints) {
            dispatchServicePointsSearch(true);
        }
    };

    const handleServicePointChange = (value: SelectValueType | SelectValueType[], error: boolean) => {
        const nextValue = Array.isArray(value) ? value[0] : value;
        props.onChange(nextValue, error);
        if (nextValue) {
            applyCountryFromPoint(String(nextValue));
        }
    };

    const handleMapSelect = (servicePointId: string) => {
        props.onChange(servicePointId, false);
        applyCountryFromPoint(servicePointId);
        setMapOpen(false);
    };

    const handleMapSearchByGeo = (latitude: number, longitude: number) => {
        if (!country) {
            return;
        }
        dispatch(
            getDhlServicePointsFetch({
                country,
                latitude,
                longitude,
                merge: true,
            })
        );
    };

    return (
        <>
            <Grid>
                <GridItem cols={3}>
                    <Select
                        options={countryOptions}
                        value={country}
                        onChange={handleChangeCountry}
                        name={`${props.name}country`}
                        label={translate('orderDetail.countryOfDelivery')}
                        required
                        disabled={props.disabled}
                        fullWidth
                        disableVirtualized
                    />
                </GridItem>
                <GridItem cols={2}>
                    <TextField
                        value={postalCode}
                        onChange={(value) => setPostalCode(String(value || ''))}
                        name={`${props.name}postalCode`}
                        label={translate('orderDetail.postCode')}
                        fullWidth
                        disabled={props.disabled}
                    />
                </GridItem>
                <GridItem cols={3}>
                    <TextField
                        value={city}
                        onChange={(value) => setCity(String(value || ''))}
                        name={`${props.name}city`}
                        label={translate('orderDetail.city')}
                        fullWidth
                        disabled={props.disabled}
                    />
                </GridItem>
                <GridItem cols={4} sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <Button
                        onClick={handleSearch}
                        disabled={props.disabled || loading || !country}
                        tooltiop={translate('orderDetail.dhlSearchServicePointsHint')}
                        sx={inputAlignedButtonSx}
                    >
                        {translate('orderDetail.dhlSearchServicePoints')}
                    </Button>
                    <Button
                        onClick={handleOpenMap}
                        disabled={props.disabled || !country}
                        tooltiop={translate('orderDetail.dhlSelectOnMapHint')}
                        variant="outlined"
                        sx={inputAlignedButtonSx}
                    >
                        {translate('orderDetail.dhlSelectOnMap')}
                    </Button>
                </GridItem>
                <GridItem cols={12}>
                    <Select
                        options={allOptions}
                        value={props.value}
                        error={props.error}
                        onChange={handleServicePointChange}
                        name={props.name}
                        label={props.label}
                        helperText={
                            points.length === 0
                                ? translate('orderDetail.dhlNoPointsLoadedHint')
                                : undefined
                        }
                        placeholder={
                            points.length > 0 &&
                            (props.value === null || props.value === undefined || props.value === '')
                                ? translate('orderDetail.dhlSelectFromListHint')
                                : undefined
                        }
                        required={props.required}
                        disabled={props.disabled}
                        fullWidth={props.fullWidth}
                        loading={loading}
                        virtualized
                    />
                </GridItem>
            </Grid>
            {mapOpen && (
                <DhlServicePointMap
                    points={points}
                    selectedId={props.value ? String(props.value) : null}
                    initialCenter={mapInitialCenter}
                    autoFitToPoints={mapAutoFitToPoints}
                    loading={loading}
                    onSelect={handleMapSelect}
                    onSearchByGeo={handleMapSearchByGeo}
                    onClose={() => setMapOpen(false)}
                />
            )}
        </>
    );
};

export default DhlServicePointSelect;
